import { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Circle, Popup, useMap, useMapEvents, Marker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api, { errorMessage } from "../lib/api";
import { PageHeader, PillBadge, GhostButton } from "../components/Primitives";
import { MultiSelectDropdown } from "../components/MultiSelectDropdown";
import { useFullscreenRoute } from "../components/StickyFiltersBar";
import { toast } from "sonner";
import { MapPin, RefreshCw, Layers, Phone, ExternalLink, Radio, X, Send, Maximize2, Minimize2, Link2 } from "lucide-react";
import { NewDispatchModal } from "../components/dispatch/NewDispatchModal";

// A guard's "status" is derived from their latest ping.
// - live      : within geofence, ping < 3 min old
// - breach    : outside geofence (any age up to 15 min)
// - stale     : ping older than STALE_MIN (5 min by default)
// - unknown   : no ping yet (checked in but hasn't reported location)
const STALE_MIN = 5;
const POLL_MS = 30000;

const STATUS_TONE = {
  live:    "#34C759",
  breach:  "#FF3B30",
  stale:   "#FF9F0A",
  unknown: "#8E8E93",
};
const STATUS_LABEL = {
  live:    "On-site",
  breach:  "Outside fence",
  stale:   "Stale ping",
  unknown: "No GPS yet",
};

function officerStatus(o) {
  if (!o.last_ping) return "unknown";
  const age = o.minutes_since_ping ?? 0;
  if (o.open_breach || o.last_ping.within_geofence === false) return "breach";
  if (age > STALE_MIN) return "stale";
  return "live";
}

// Auto-fit bounds to include every officer + their site geo circle.
function FitBounds({ officers }) {
  const map = useMap();
  useEffect(() => {
    const pts = [];
    for (const o of officers) {
      if (o.site_lat != null && o.site_lng != null) pts.push([o.site_lat, o.site_lng]);
      if (o.last_ping?.lat != null && o.last_ping?.lng != null) pts.push([o.last_ping.lat, o.last_ping.lng]);
    }
    if (!pts.length) return;
    if (pts.length === 1) {
      map.setView(pts[0], 14);
      return;
    }
    map.fitBounds(pts, { padding: [40, 40], maxZoom: 14 });
  }, [officers, map]);
  return null;
}

const STATUS_FILTER_OPTIONS = [
  { value: "live",    label: "On-site" },
  { value: "breach",  label: "Outside fence" },
  { value: "stale",   label: "Stale ping" },
  { value: "unknown", label: "No GPS yet" },
];

// Custom pulsing icon for the dispatch pin.
const DISPATCH_ICON = new L.DivIcon({
  className: "",
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  html: '<div style="width:28px;height:28px;border-radius:50%;background:#0A84FF;border:3px solid #ffffff;box-shadow:0 0 0 4px rgba(10,132,255,0.25);animation:iris-pulse 1.4s infinite ease-out;"></div>'
       + '<style>@keyframes iris-pulse{0%{box-shadow:0 0 0 0 rgba(10,132,255,0.5);}70%{box-shadow:0 0 0 18px rgba(10,132,255,0);}100%{box-shadow:0 0 0 0 rgba(10,132,255,0);}}</style>',
});

function DispatchClickHandler({ enabled, onPick }) {
  useMapEvents({
    click(e) {
      if (!enabled) return;
      onPick({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

export default function LiveMap() {
  const { pathname } = useLocation();
  const isFs = useFullscreenRoute(pathname);
  const onFs = () => (isFs ? window.close() : window.open("/live-map/fullscreen", "_blank", "noopener"));
  // ── URL <-> state sync (deep-linkable filters) ────────────────────────
  // Filters serialise into the querystring so supervisors can bookmark or
  // share a view: `?clients=a,b&sites=x&statuses=breach&tile=light`.
  const [searchParams, setSearchParams] = useSearchParams();
  const csv = (key) => {
    const v = searchParams.get(key);
    return v ? v.split(",").filter(Boolean) : [];
  };
  const [officers, setOfficers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastFetch, setLastFetch] = useState(null);
  // Map defaults to light tiles everywhere; users can flip to dark and the
  // choice persists via the `?tile=dark` query param for shareable links.
  const [tileLight, setTileLight] = useState(searchParams.get("tile") !== "dark");
  const [clientFilter, setClientFilter] = useState(csv("clients"));
  const [siteFilter, setSiteFilter] = useState(csv("sites"));
  const [statusFilter, setStatusFilter] = useState(csv("statuses"));
  // ── Radius dispatch ───────────────────────────────────────────────────
  const [dispatchMode, setDispatchMode] = useState(false);
  const [dispatchPin, setDispatchPin] = useState(null);       // {lat, lng}
  const [dispatchLimit, setDispatchLimit] = useState(5);
  const [dispatchCandidates, setDispatchCandidates] = useState([]);
  const [dispatchSelected, setDispatchSelected] = useState(new Set());
  const [dispatchSubject, setDispatchSubject] = useState("");
  const [dispatchMessage, setDispatchMessage] = useState("");
  const [dispatchChannels, setDispatchChannels] = useState(new Set(["push", "email"]));
  const [dispatchLoading, setDispatchLoading] = useState(false);
  const [dispatchSending, setDispatchSending] = useState(false);
  // Site-scoped dispatch launched from a guard popover — reuses the
  // <NewDispatchModal restrictToSite> flow so the blast is auto-limited
  // to guards currently on-duty at that site.
  const [dispatchForSite, setDispatchForSite] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/live-map/officers");
      setOfficers(r.data || []);
      setLastFetch(new Date());
    } catch (e) {
      toast.error(errorMessage(e, "Failed to load live map"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  // Write filters back to the URL whenever they change. `replace: true` avoids
  // polluting history — using the browser Back button should exit the page,
  // not walk through filter revisions.
  useEffect(() => {
    const next = new URLSearchParams();
    if (clientFilter.length) next.set("clients", clientFilter.join(","));
    if (siteFilter.length) next.set("sites", siteFilter.join(","));
    if (statusFilter.length) next.set("statuses", statusFilter.join(","));
    if (!tileLight) next.set("tile", "dark");
    setSearchParams(next, { replace: true });
  }, [clientFilter, siteFilter, statusFilter, tileLight, setSearchParams]);

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied — paste into Slack/email to share this view");
    } catch {
      toast.error("Clipboard blocked — copy URL from the address bar");
    }
  }, []);

  const onDispatchPick = useCallback(async ({ lat, lng }) => {
    setDispatchPin({ lat, lng });
    setDispatchLoading(true);
    try {
      const r = await api.post("/dispatch/nearest", { lat, lng, limit: dispatchLimit });
      const guards = r.data?.guards || [];
      setDispatchCandidates(guards);
      setDispatchSelected(new Set(guards.map((g) => g.guard_id)));
    } catch (e) {
      toast.error(errorMessage(e, "Failed to fetch nearest "));
      setDispatchCandidates([]);
      setDispatchSelected(new Set());
    } finally {
      setDispatchLoading(false);
    }
  }, [dispatchLimit]);

  const toggleDispatchGuard = useCallback((gid) => {
    setDispatchSelected((prev) => {
      const next = new Set(prev);
      if (next.has(gid)) next.delete(gid); else next.add(gid);
      return next;
    });
  }, []);

  const toggleDispatchChannel = useCallback((ch) => {
    setDispatchChannels((prev) => {
      const next = new Set(prev);
      if (next.has(ch)) next.delete(ch); else next.add(ch);
      return next;
    });
  }, []);

  const closeDispatch = useCallback(() => {
    setDispatchPin(null);
    setDispatchCandidates([]);
    setDispatchSelected(new Set());
    setDispatchSubject("");
    setDispatchMessage("");
  }, []);

  const sendDispatch = useCallback(async () => {
    if (!dispatchPin) return;
    const guard_ids = [...dispatchSelected];
    if (guard_ids.length === 0) {
      toast.error("Select at least one ");
      return;
    }
    if (!dispatchMessage.trim()) {
      toast.error("Message is required");
      return;
    }
    if (dispatchChannels.size === 0) {
      toast.error("Pick at least one channel (Push or Email)");
      return;
    }
    setDispatchSending(true);
    try {
      const r = await api.post("/dispatch/broadcast", {
        lat: dispatchPin.lat,
        lng: dispatchPin.lng,
        guard_ids,
        subject: dispatchSubject.trim() || undefined,
        message: dispatchMessage.trim(),
        channels: [...dispatchChannels],
      });
      const { push_sent, email_sent } = r.data || {};
      toast.success(`Dispatch sent — ${push_sent} push · ${email_sent} email`);
      closeDispatch();
    } catch (e) {
      toast.error(errorMessage(e, "Dispatch failed"));
    } finally {
      setDispatchSending(false);
    }
  }, [dispatchPin, dispatchSelected, dispatchMessage, dispatchSubject, dispatchChannels, closeDispatch]);

  // Filter options derived from data. Use client_id when available, else fall
  // back to client_name so legacy sites without a resolved client_id still
  // participate in filtering.
  const clientOptions = useMemo(() => {
    const m = new Map();
    officers.forEach((o) => {
      const key = o.client_id || o.client_name;
      if (key) m.set(key, o.client_name || key);
    });
    return [...m.entries()].map(([value, label]) => ({ value, label }));
  }, [officers]);
  const siteOptions = useMemo(() => {
    const m = new Map();
    officers.forEach((o) => { if (o.site_id) m.set(o.site_id, o.site_name || o.site_id); });
    return [...m.entries()].map(([value, label]) => ({ value, label }));
  }, [officers]);

  const filtered = useMemo(() => {
    return officers.filter((o) => {
      if (clientFilter.length) {
        const oKey = o.client_id || o.client_name;
        if (!clientFilter.includes(oKey)) return false;
      }
      if (siteFilter.length && !siteFilter.includes(o.site_id)) return false;
      if (statusFilter.length && !statusFilter.includes(officerStatus(o))) return false;
      return true;
    });
  }, [officers, clientFilter, siteFilter, statusFilter]);

  // Only officers we can actually plot (ping OR site coords available).
  const plotted = useMemo(() => filtered.filter(
    (o) => (o.last_ping?.lat != null && o.last_ping?.lng != null) ||
           (o.site_lat != null && o.site_lng != null)
  ), [filtered]);

  const stats = useMemo(() => {
    const s = { live: 0, breach: 0, stale: 0, unknown: 0 };
    for (const o of filtered) s[officerStatus(o)]++;
    return s;
  }, [filtered]);

  return (
    <div data-testid="live-map-page">
      <PageHeader
        title="Live Officers Map"
        subtitle={`Real-time location of clocked-in guards. Auto-refreshes every ${POLL_MS / 1000}s.`}
        action={
          <div className="inline-flex items-center gap-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1" data-testid="live-map-refresh-indicator">
              <RefreshCw className="size-3" /> {lastFetch ? `Updated ${lastFetch.toLocaleTimeString()}` : "Loading…"}
            </div>
            <GhostButton data-testid="live-map-refresh" onClick={load}>Refresh</GhostButton>
            <GhostButton
              data-testid="live-map-copy-link"
              onClick={copyLink}
              title="Copy a shareable link with current filters"
            >
              <Link2 className="size-3.5" /> Copy link
            </GhostButton>
            <GhostButton
              data-testid={isFs ? "live-map-exit-fullscreen" : "live-map-fullscreen"}
              onClick={onFs}
              title={isFs ? "Close fullscreen" : "Open in fullscreen"}
            >
              {isFs ? <><Minimize2 className="size-3.5" /> Close</> : <><Maximize2 className="size-3.5" /> Fullscreen</>}
            </GhostButton>
            <GhostButton
              data-testid="live-map-dispatch-toggle"
              onClick={() => { setDispatchMode((v) => !v); if (dispatchMode) closeDispatch(); }}
              title={dispatchMode ? "Exit dispatch mode" : "Click anywhere on the map to find the nearest on-shift guards"}
              style={dispatchMode ? { color: "#0A84FF", borderColor: "#0A84FF" } : undefined}
            >
              <Radio className="size-3" /> {dispatchMode ? "Dispatch: ON" : "Dispatch"}
            </GhostButton>
          </div>
        }
      />
      <div className="p-8 space-y-4">
        {/* Stat tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {(["live", "breach", "stale", "unknown"]).map((k) => (
            <div
              key={k}
              className="border border-[#27272A] bg-[#121212] px-4 py-3"
              data-testid={`live-map-stat-${k}`}
            >
              <div className="label-mute flex items-center gap-1.5" style={{ color: STATUS_TONE[k] }}>
                <span className="inline-block size-2 rounded-full" style={{ background: STATUS_TONE[k] }} />
                {STATUS_LABEL[k]}
              </div>
              <div className="font-display text-2xl font-bold text-zinc-100 mt-1">{stats[k]}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <MultiSelectDropdown
            testId="live-map-client"
            label="All clients"
            options={clientOptions}
            selected={clientFilter}
            onChange={setClientFilter}
            searchable
            triggerClassName="w-full"
          />
          <MultiSelectDropdown
            testId="live-map-site"
            label="All sites"
            options={siteOptions}
            selected={siteFilter}
            onChange={setSiteFilter}
            searchable
            triggerClassName="w-full"
          />
          <MultiSelectDropdown
            testId="live-map-status"
            label="All statuses"
            options={STATUS_FILTER_OPTIONS}
            selected={statusFilter}
            onChange={setStatusFilter}
            triggerClassName="w-full"
          />
          <div className="flex items-center gap-2">
            <button
              onClick={() => setTileLight((v) => !v)}
              data-testid="live-map-tile-toggle"
              className="inline-flex items-center gap-2 px-3 py-2 border border-[#27272A] text-[11px] uppercase tracking-widest text-zinc-300 hover:text-white hover:border-[#0A84FF]"
            >
              <Layers className="size-3" /> {tileLight ? "Dark map" : "Light map"}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Map */}
          <div className="lg:col-span-3 border border-[#27272A] bg-[#121212] p-2" data-testid="live-map-container">
            <div style={{ height: "580px", width: "100%" }}>
              <MapContainer center={[39.8283, -98.5795]} zoom={4} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
                {tileLight ? (
                  <TileLayer attribution="&copy; OSM" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                ) : (
                  <TileLayer attribution="&copy; Carto" url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" />
                )}
                <FitBounds officers={plotted} />
                <DispatchClickHandler enabled={dispatchMode} onPick={onDispatchPick} />
                {dispatchPin && (
                  <>
                    <Marker position={[dispatchPin.lat, dispatchPin.lng]} icon={DISPATCH_ICON} />
                    {/* 5 km radius ring so ops sees the search area */}
                    <Circle
                      center={[dispatchPin.lat, dispatchPin.lng]}
                      radius={5000}
                      pathOptions={{ color: "#0A84FF", fillColor: "#0A84FF", fillOpacity: 0.04, weight: 1, dashArray: "3 4" }}
                    />
                  </>
                )}
                {/* Site geofence circles + centre markers, only for sites currently hosting a guard */}
                {plotted.map((o) => (
                  o.site_lat != null && o.site_lng != null && (
                    <Circle
                      key={`fence-${o.site_id}`}
                      center={[o.site_lat, o.site_lng]}
                      radius={o.fence_m}
                      pathOptions={{ color: "#0A84FF", fillColor: "#0A84FF", fillOpacity: 0.08, weight: 1, dashArray: "6 4" }}
                    />
                  )
                ))}
                {/* Officer markers */}
                {plotted.map((o) => {
                  const st = officerStatus(o);
                  const tone = STATUS_TONE[st];
                  const lat = o.last_ping?.lat ?? o.site_lat;
                  const lng = o.last_ping?.lng ?? o.site_lng;
                  if (lat == null || lng == null) return null;
                  return (
                    <CircleMarker
                      key={`${o.shift_id}-${o.guard_id}`}
                      center={[lat, lng]}
                      radius={11}
                      pathOptions={{ color: tone, fillColor: tone, fillOpacity: 0.9, weight: 3 }}
                    >
                      <Popup>
                        <div className="text-xs" style={{ minWidth: 200 }} data-testid={`live-map-popup-${o.guard_id}`}>
                          <div className="font-bold text-sm mb-1">{o.guard_name || "Unknown guard"}</div>
                          <div className="text-zinc-600 mb-2 font-mono text-[10px]">{o.badge_no || "—"}</div>
                          <div className="text-zinc-700 mb-1">{o.site_name} · {o.client_name || "—"}</div>
                          <div className="mt-2 flex items-center gap-2 flex-wrap">
                            <PillBadge tone={st === "breach" ? "red" : st === "stale" ? "amber" : st === "live" ? "green" : "default"}>
                              {STATUS_LABEL[st]}
                            </PillBadge>
                            {o.last_ping?.distance_m != null && (
                              <span className="text-[10px] uppercase tracking-widest text-zinc-600">{o.last_ping.distance_m}m from site</span>
                            )}
                            {o.minutes_since_ping != null && (
                              <span className="text-[10px] uppercase tracking-widest text-zinc-600">ping {o.minutes_since_ping}m ago</span>
                            )}
                          </div>
                          <div className="mt-3 flex items-center gap-2">
                            {o.guard_phone && (
                              <a
                                href={`tel:${o.guard_phone}`}
                                data-testid={`live-map-call-${o.guard_id}`}
                                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#0A84FF] hover:underline"
                              >
                                <Phone className="size-3" /> Call
                              </a>
                            )}
                            <a
                              href={`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`}
                              target="_blank" rel="noopener noreferrer"
                              data-testid={`live-map-directions-${o.guard_id}`}
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#0A84FF] hover:underline"
                            >
                              <ExternalLink className="size-3" /> Directions
                            </a>
                            {o.site_id && (
                              <button
                                type="button"
                                onClick={() => setDispatchForSite({ id: o.site_id, name: o.site_name })}
                                data-testid={`live-map-dispatch-${o.site_id}`}
                                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#0A84FF] hover:underline"
                              >
                                <Radio className="size-3" /> Dispatch on-duty
                              </button>
                            )}
                          </div>
                        </div>
                      </Popup>
                    </CircleMarker>
                  );
                })}
              </MapContainer>
            </div>
            <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-500 flex items-center justify-between">
              <span data-testid="live-map-count">
                {loading ? "Loading…" : `${plotted.length} on-site / ${filtered.length} matched / ${officers.length} total`}
              </span>
              <span className="text-zinc-600">Blue dashed circles = site geofence</span>
            </div>
          </div>

          {/* Officer list */}
          <div className="lg:col-span-1 border border-[#27272A] bg-[#121212] max-h-[620px] overflow-y-auto" data-testid="live-map-list">
            <div className="p-3 label-mute border-b border-[#27272A]">Officers ({filtered.length})</div>
            {filtered.length === 0 && (
              <div className="p-6 text-center text-zinc-500 text-sm scanlines">No officers currently on-shift.</div>
            )}
            {filtered.map((o) => {
              const st = officerStatus(o);
              return (
                <div key={`${o.shift_id}-${o.guard_id}`} className="p-3 border-b border-[#27272A] hover:bg-[#1c1c1e]" data-testid={`live-map-officer-${o.guard_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-display font-bold uppercase tracking-tight truncate">{o.guard_name || "—"}</div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono truncate">{o.badge_no || "—"} · {o.site_name}</div>
                    </div>
                    <span className="size-2.5 rounded-full flex-none" style={{ background: STATUS_TONE[st] }} title={STATUS_LABEL[st]} />
                  </div>
                  <div className="mt-2 flex items-center gap-2 flex-wrap text-[10px] uppercase tracking-widest text-zinc-500">
                    <span style={{ color: STATUS_TONE[st] }}>{STATUS_LABEL[st]}</span>
                    {o.last_ping?.distance_m != null && <span>· {o.last_ping.distance_m}m</span>}
                    {o.minutes_since_ping != null && <span>· ping {o.minutes_since_ping}m ago</span>}
                    {o.open_breach && (
                      <PillBadge tone="red" data-testid={`live-map-breach-badge-${o.guard_id}`}>
                        <MapPin className="size-3 mr-1" /> Breach
                      </PillBadge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Radius Dispatch modal ─────────────────────────────────────── */}
      {dispatchPin && (
        <div
          className="fixed inset-0 z-[9000] bg-black/70 flex items-center justify-center p-4"
          onClick={closeDispatch}
          data-testid="dispatch-modal-backdrop"
        >
          <div
            className="border border-[#27272A] bg-[#0A0A0A] w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="dispatch-modal"
          >
            <div className="flex items-center justify-between border-b border-[#27272A] px-5 py-4">
              <div>
                <div className="label-mute flex items-center gap-2"><Radio className="size-3" /> Radius Dispatch</div>
                <div className="font-display font-bold text-xl uppercase tracking-tight mt-1">
                  {dispatchPin.lat.toFixed(5)}, {dispatchPin.lng.toFixed(5)}
                </div>
              </div>
              <button
                onClick={closeDispatch}
                data-testid="dispatch-modal-close"
                className="text-zinc-500 hover:text-white p-1"
              >
                <X className="size-4" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Limit selector */}
              <div className="flex items-center gap-2">
                <div className="label-mute">Show nearest</div>
                {[3, 5, 10, 20].map((n) => (
                  <button
                    key={n}
                    onClick={() => { setDispatchLimit(n); onDispatchPick(dispatchPin); }}
                    data-testid={`dispatch-limit-${n}`}
                    className={`px-2.5 py-1 text-[10px] uppercase tracking-widest border ${dispatchLimit === n ? "border-[#0A84FF] text-white" : "border-[#27272A] text-zinc-400 hover:text-zinc-200"}`}
                  >{n}</button>
                ))}
              </div>

              {/* Candidate list */}
              <div className="border border-[#27272A] max-h-56 overflow-y-auto" data-testid="dispatch-candidates">
                {dispatchLoading && (
                  <div className="p-4 text-center text-zinc-500 text-sm scanlines">Locating nearest …</div>
                )}
                {!dispatchLoading && dispatchCandidates.length === 0 && (
                  <div className="p-4 text-center text-zinc-500 text-sm">No on-shift  found in your scope.</div>
                )}
                {dispatchCandidates.map((g) => {
                  const checked = dispatchSelected.has(g.guard_id);
                  return (
                    <label
                      key={g.guard_id}
                      className={`flex items-center gap-3 p-3 border-b border-[#27272A] cursor-pointer hover:bg-[#1c1c1e] ${checked ? "bg-[#0A84FF15]" : ""}`}
                      data-testid={`dispatch-candidate-${g.guard_id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleDispatchGuard(g.guard_id)}
                        className="accent-[#0A84FF]"
                        data-testid={`dispatch-check-${g.guard_id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-bold uppercase tracking-tight truncate">{g.guard_name || "—"}</div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono truncate">
                          {g.badge_no || "—"} · {g.site_name} · {g.client_name || "—"}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono text-sm text-[#0A84FF] font-bold">{g.distance_km} km</div>
                        {g.guard_phone && <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">{g.guard_phone}</div>}
                      </div>
                    </label>
                  );
                })}
              </div>

              {/* Message form */}
              <input
                type="text"
                placeholder="Subject (optional)"
                value={dispatchSubject}
                onChange={(e) => setDispatchSubject(e.target.value)}
                data-testid="dispatch-subject"
                className="w-full bg-[#121212] border border-[#27272A] px-3 py-2 text-sm text-zinc-200 focus:border-[#0A84FF] focus:outline-none"
                maxLength={80}
              />
              <textarea
                placeholder="Dispatch message — what should the  do?"
                value={dispatchMessage}
                onChange={(e) => setDispatchMessage(e.target.value)}
                data-testid="dispatch-message"
                rows={4}
                className="w-full bg-[#121212] border border-[#27272A] px-3 py-2 text-sm text-zinc-200 focus:border-[#0A84FF] focus:outline-none resize-none"
                maxLength={500}
              />

              {/* Channels + send */}
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="inline-flex items-center gap-2">
                  <div className="label-mute mr-1">Channels</div>
                  {["push", "email"].map((ch) => {
                    const on = dispatchChannels.has(ch);
                    return (
                      <button
                        key={ch}
                        onClick={() => toggleDispatchChannel(ch)}
                        data-testid={`dispatch-channel-${ch}`}
                        className={`px-3 py-1 text-[10px] uppercase tracking-widest border ${on ? "border-[#0A84FF] text-white bg-[#0A84FF20]" : "border-[#27272A] text-zinc-500"}`}
                      >
                        {ch}
                      </button>
                    );
                  })}
                </div>
                <div className="inline-flex items-center gap-2">
                  <GhostButton onClick={closeDispatch} data-testid="dispatch-cancel">Cancel</GhostButton>
                  <button
                    onClick={sendDispatch}
                    disabled={dispatchSending || dispatchSelected.size === 0 || !dispatchMessage.trim()}
                    data-testid="dispatch-send"
                    className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest font-bold bg-[#0A84FF] text-white disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Send className="size-3" />
                    {dispatchSending ? "Sending…" : `Blast ${dispatchSelected.size} guard${dispatchSelected.size === 1 ? "" : "s"}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {dispatchForSite && (
        <NewDispatchModal
          restrictToSite={dispatchForSite}
          onClose={() => setDispatchForSite(null)}
          onSent={() => setDispatchForSite(null)}
        />
      )}
    </div>
  );
}
